import axios from 'axios';
import { API_BASE } from './config';

/**
 * Reset a stuck deposit transaction by ID
 * 
 * @param id Transaction ID
 * @returns Reset result
 */
export const resetDepositTransaction = async (id: string) => {
  try {
    console.log(`Resetting deposit transaction: ${id}`);

    const response = await axios.post(`${API_BASE}/deposit/reset/${id}`);

    console.log('Reset transaction API response:', response.data);

    return response.data;
  } catch (error) {
    console.error('Error in resetDepositTransaction API call:', error);

    // If it's an Axios error with a response, return the error response
    if (axios.isAxiosError(error) && error.response) {
      console.error('API error response:', error.response.data);
      return error.response.data;
    }

    // Otherwise, return a formatted error
    return {
      status: false,
      error: error instanceof Error ? error.message : 'Unknown error',
      errorType: 'API_ERROR',
      details: { originalError: error }
    };
  }
};

/**
 * Reset stuck deposit transactions
 * 
 * @param options Reset options
 * @returns Reset result
 */
export const resetStuckDepositTransactions = async (options: {
  olderThanMinutes?: number;
  statuses?: string[];
} = {}) => {
  try {
    const { olderThanMinutes = 10, statuses = ['Detected', 'Confirmed', 'Processed', 'Failed', 'Pending'] } = options;

    console.log(`Resetting stuck deposit transactions older than ${olderThanMinutes} minutes with statuses: ${statuses.join(', ')}`);

    const response = await axios.post(`${API_BASE}/deposit/reset-stuck`, {
      olderThanMinutes,
      statuses
    });

    console.log('Reset stuck transactions API response:', response.data);

    return response.data;
  } catch (error) {
    console.error('Error in resetStuckDepositTransactions API call:', error);

    // If it's an Axios error with a response, return the error response
    if (axios.isAxiosError(error) && error.response) {
      console.error('API error response:', error.response.data);
      return error.response.data;
    }

    // Otherwise, return a formatted error
    return {
      status: false,
      error: error instanceof Error ? error.message : 'Unknown error',
      errorType: 'API_ERROR',
      details: { originalError: error }
    };
  }
};

/**
 * Fetch deposit monitoring data with detailed stage information
 *
 * @param filters Filter criteria
 * @param limit Maximum number of deposits to return
 * @param skip Number of deposits to skip (for pagination)
 * @param sort Sort configuration
 * @returns Deposit monitoring data
 */
export const fetchDepositMonitor = async (
  filters: any = {},
  limit: number = 50,
  skip: number = 0,
  sort?: { field: string; order: 'asc' | 'desc' }
) => {
  try {
    console.log('Fetching deposit monitor data with filters:', filters);

    // Build query parameters
    const params: any = {
      page: Math.floor(skip / limit) + 1,
      limit
    };

    // Add filters to params
    if (filters.status) params.status = filters.status;
    if (filters.callback_status) params.callback_status = filters.callback_status;
    if (filters.user_id) params.user_id = filters.user_id;
    if (filters.txid) params.txid = filters.txid;
    if (filters.address) params.address = filters.address;
    if (filters.blockchain) params.blockchain = filters.blockchain;
    if (filters.from_date) params.from_date = filters.from_date;
    if (filters.to_date) params.to_date = filters.to_date;

    const response = await axios.get(`${API_BASE}/deposit/processing-stages`, {
      params
    });

    console.log('Deposit monitor API response:', response.data);

    return response.data;
  } catch (error) {
    console.error('Error in fetchDepositMonitor API call:', error);

    // If it's an Axios error with a response, return the error response
    if (axios.isAxiosError(error) && error.response) {
      console.error('API error response:', error.response.data);
      return error.response.data;
    }

    // Otherwise, return a formatted error
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
      errorType: 'API_ERROR',
      details: { originalError: error }
    };
  }
};
